import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';

import AnimatedCursor from './AnimatedCursor';
import {
  ACTION_FAILURE,
  ACTION_SUCCESS,
  buildDriverSteps,
  elementCenter,
  evaluateStepGate,
  resolveStepElement,
  waitForTutorialActionResult,
  waitForStepElement,
} from './tourEngine';

/**
 * How a run ended. ONE value, set at each terminal site and read once on teardown.
 *
 * This replaces a bare `finishedRef` boolean whose writers raced each other, so
 * the outcome reported to the container was whatever happened to write last.
 * Naming the outcomes makes each terminal site state what it means, and makes
 * "exited because the action failed" distinguishable from "the user chose to
 * leave".
 *
 * Only ONE of these is a completion, and only two sites set it: driver.js's Done
 * button, and `advance()` running off the end of the steps. Everything else here
 * is teardown. A tutorial whose taught action is asynchronous does not complete
 * from this runner at all — `tutorialOutcomes.js` reports the real outcome and
 * the container's `completesOn` listener records it, which is also why that
 * listener has to outlive the tour.
 */
export const OUTCOME = {
  COMPLETED: 'completed',
  USER_EXITED: 'user_exited',
  ACTION_FAILED: 'action_failed',
  STEP_UNAVAILABLE: 'step_unavailable',
  // Teardown that must report nothing at all (unmount, route change, rebuild).
  SILENT: 'silent',
};

/** How long the fake cursor glides before it plays its click pulse. */
export const CURSOR_GLIDE_MS = 620;
/** How long the click pulse runs before the user is handed the real click. */
export const CURSOR_PULSE_MS = 420;
/**
 * A required target gets a generous window: it usually appears as a result of
 * the previous step's real click (modal render, dropdown open, selection-gated
 * button). An optional one gets far less — if it isn't there almost immediately
 * it is skipped rather than stalling the tour.
 */
export const REQUIRED_STEP_WAIT_MS = 8000;
export const OPTIONAL_STEP_WAIT_MS = 1200;
/**
 * Deliberate pause between a step being satisfied and the spotlight moving on.
 *
 * The click that advances a step usually re-renders a chunk of the page (a modal
 * opens, a row re-renders, a toolbar swaps). Moving the spotlight immediately
 * makes driver.js measure mid-re-render, which is what forced the corrective
 * refresh passes that caused the visible flicker. Letting the DOM settle first
 * means the stage is measured once, correctly — and the transition reads as
 * deliberate rather than jumpy.
 */
export const STEP_SETTLE_MS = 320;
/**
 * How long to let driver.js's own highlight-transfer animation (~400ms) finish
 * before the rect tracker is allowed to refresh. A refresh landing mid-animation
 * is either ignored outright or bakes in a half-animated popover position, so
 * the tracker used to fire four unconditional refreshes to brute-force past it —
 * the main source of the flicker. Starting after the animation makes one enough.
 */
export const TARGET_TRACK_START_MS = 480;
/** Cadence of the rect tracker once it has started. */
export const TARGET_TRACK_INTERVAL_MS = 250;
/**
 * Movement (px, either axis) required to count as "the target actually moved".
 * Sub-pixel layout jitter re-triggering a refresh is itself a flicker source.
 */
export const TARGET_MOVE_EPSILON_PX = 2;
/**
 * Consecutive highlight-guard ticks (200ms apart) on which the final step's
 * target must be gone before the tutorial is treated as finished. Guards against
 * the momentary detachment every antd re-render produces.
 */
export const VANISHED_TARGET_TICKS = 3;
/**
 * How many times ONE step may rebuild the whole driver.js tour.
 *
 * A rebuild destroys and recreates the tour (`createDriver()` + `drive(index)`),
 * and both callers can ask for one repeatedly — the highlight guard every 200ms,
 * the post-transition self-heal twice per step. Sustained node replacement (what
 * hovering an antd menu item produces) therefore turns into an unbounded rebuild
 * loop unless the budget is bounded. A few attempts recover the genuine case;
 * past that, rebuilding is not working and repeating it only burns the main
 * thread.
 */
export const MAX_STEP_REBUILDS = 3;
/**
 * Delay before the popover is allowed to glide between positions. driver.js sets
 * the popover's `top`/`left` inline with no transition, so it teleports to each
 * new step while the spotlight cut-out animates — the mismatch is what reads as
 * a flicker. A CSS transition (see tutorial.scss) smooths it, but it must not be
 * active for the popover's FIRST placement or it would slide in from driver.js's
 * default top-right corner. So the class is added shortly after the first
 * highlight has painted.
 */
export const POPOVER_GLIDE_ARM_MS = 80;

/**
 * Runs one tutorial: driver.js provides the dimmed backdrop, spotlight cut-out
 * and tooltip; this component adds the animated cursor, click-to-advance
 * handling, and guarantees teardown.
 *
 * Step progression is driven here rather than by driver.js's own buttons so the
 * spotlight never moves to an element that hasn't rendered yet — see
 * `waitForStepElement`.
 */
const useTutorialDriver = ({
  tutorial, onComplete, onExit, onUnavailable, onBlocked,
}) => {
  const [cursor, setCursor] = useState({ point: null, pulsing: false, visible: false });

  const driverRef = useRef(null);
  const stepsRef = useRef([]);
  // How this run ended (see OUTCOME). Defaults to a user exit: if a tour is torn
  // down by a path that never states an outcome, the safe reading is "the user
  // left", never "the lesson was completed".
  const outcomeRef = useRef(OUTCOME.USER_EXITED);
  const timersRef = useRef([]);
  /**
   * Aborts in-flight `waitForStepElement` polls.
   *
   * Those polls run on their own `setInterval`, which `clearTimers()` cannot touch,
   * so an abandoned wait kept querying the DOM every 100ms for the rest of its 8s
   * budget after the tour had already been torn down. Re-created per tour.
   */
  const waitAbortRef = useRef(null);
  const detachClickRef = useRef(null);
  // Set by the main effect; lets advance() rebuild the tour at a given step.
  const rebuildAtRef = useRef(null);
  /**
   * Rebuild budget, per step, OUTSIDE the closures that spend it.
   *
   * This was a `let rebuildsLeft = 3` local to `handleHighlighted` — which is the
   * function a rebuild re-enters. `rebuildAtRef.current(index)` calls
   * `drive(index)`, driver.js fires `onHighlighted`, and the counter was
   * initialised to 3 all over again: the cap could never be reached, so the
   * 200ms loop it claims to bound was still unbounded. Keyed by tutorial + step
   * so it survives a rebuild (same key) and resets on a genuine step transition
   * (different key), which is the distinction the local could not express.
   */
  const rebuildBudgetRef = useRef({ key: null, left: 0 });
  const mountedRef = useRef(true);
  // The popover node the position transition has been armed for.
  const popoverNodeRef = useRef(null);

  const clearTimers = useCallback(() => {
    timersRef.current.forEach((id) => clearTimeout(id));
    timersRef.current = [];
  }, []);

  const schedule = useCallback((fn, delay) => {
    const id = setTimeout(() => {
      timersRef.current = timersRef.current.filter((timerId) => timerId !== id);
      fn();
    }, delay);
    timersRef.current.push(id);
    return id;
  }, []);

  /**
   * Tracked `setTimeout` as a promise. Registered in `timersRef` so teardown
   * cancels it; a cancelled wait simply never resolves, which stops the
   * in-flight `advance()` exactly where we want it to stop.
   */
  const wait = useCallback((delay) => new Promise((resolve) => {
    const id = setTimeout(() => {
      timersRef.current = timersRef.current.filter((timerId) => timerId !== id);
      resolve();
    }, delay);
    timersRef.current.push(id);
  }), []);

  const detachClickHandler = useCallback(() => {
    if (detachClickRef.current) {
      detachClickRef.current();
      detachClickRef.current = null;
    }
  }, []);

  const safeSetCursor = useCallback((next) => {
    if (mountedRef.current) setCursor(next);
  }, []);

  /**
   * Arms the popover's position transition (see POPOVER_GLIDE_ARM_MS).
   *
   * Keyed on the popover NODE, not a boolean: `rebuildAt` destroys and recreates
   * the driver, which creates a fresh, unpositioned popover. Treating that new
   * node as "already armed" would glide it in from the corner.
   */
  const armPopoverGlide = useCallback(() => {
    const popover = document.querySelector('.driver-popover.tutorial-popover');
    if (!popover || popoverNodeRef.current === popover) return;
    popoverNodeRef.current = popover;
    popover.classList.remove('tutorial-popover--glide');
    schedule(() => {
      if (popoverNodeRef.current === popover) popover.classList.add('tutorial-popover--glide');
    }, POPOVER_GLIDE_ARM_MS);
  }, [schedule]);

  /**
   * Flags the popover as waiting on the next target.
   *
   * Set imperatively on the DOM node rather than through React state because
   * driver.js owns the popover element; tutorial.scss dims the footer buttons and
   * shows a "Loading…" cue off this attribute.
   */
  const setPopoverBusy = useCallback((busy) => {
    const popover = document.querySelector('.driver-popover.tutorial-popover');
    if (!popover) return;
    if (busy) popover.setAttribute('data-tutorial-busy', '');
    else popover.removeAttribute('data-tutorial-busy');
  }, []);

  /**
   * Pins the step target's clickability with a custom attribute.
   *
   * driver.js keeps the target clickable through its `driver-active-element`
   * class (`.driver-active * { pointer-events: none }` with an exception for
   * that class) — but React re-renders rewrite `className` (hovering an antd
   * menu item does this) and strip it, so for stretches of up to 200ms (the
   * highlight guard's cadence) pointer events die on the target and the
   * user's click falls through to the overlay, which exits the tour. A custom
   * attribute set imperatively is NOT managed by React, so it survives those
   * re-renders; tutorial.scss carries the matching `pointer-events: auto`
   * rule.
   */
  const markActiveTarget = useCallback((element) => {
    document.querySelectorAll('[data-tutorial-highlight]').forEach((el) => {
      if (el !== element) el.removeAttribute('data-tutorial-highlight');
    });
    if (element && element.setAttribute) element.setAttribute('data-tutorial-highlight', '');
  }, []);

  /**
   * The ONE way to rebuild the tour, so the budget cannot be routed around.
   *
   * Both callers — the highlight guard (every 200ms) and the post-transition
   * self-heal (twice per step) — go through here. `handleHighlighted` owns
   * resetting the budget; this only ever spends it, so a rebuild re-entering
   * `handleHighlighted` at the SAME index finds the same key and the same
   * remaining count.
   *
   * Returns whether the rebuild actually happened, so callers can fall through to
   * their own terminal handling once the budget is spent.
   */
  const requestRebuild = useCallback((index) => {
    const budget = rebuildBudgetRef.current;
    if (!rebuildAtRef.current || budget.left <= 0) return false;
    budget.left -= 1;
    rebuildAtRef.current(index);
    return true;
  }, []);

  /**
   * Advances past `fromIndex` to the next step whose target actually exists.
   *
   * Optional steps are resolved HERE rather than being filtered out before the
   * tour starts: their targets (an upload modal, an opened dropdown) only exist
   * as a consequence of an earlier step's real click, so a start-time filter
   * would always drop them and silently shorten every tutorial.
   */
  const advance = useCallback(async (fromIndex) => {
    const driverObj = driverRef.current;
    if (!driverObj) return;

    // A step can require more than "its target was clicked": the assign-record
    // tutorial needs the SELECTION to be one Assign Document accepts, and ticking
    // any checkbox satisfies the step without satisfying that. Checked before
    // anything is torn down, so a blocked step keeps its spotlight, its cursor and
    // its trackers and the user can simply fix the selection and press Next again.
    const blockedReason = evaluateStepGate(stepsRef.current[fromIndex]);
    if (blockedReason) {
      onBlocked(blockedReason);
      return;
    }

    detachClickHandler();
    // Mark the popover busy for the whole hunt for the next target. Without it a
    // Next click that has to wait (a list refetching after a filter change, a
    // modal still opening) looks like a dead button for up to 8 seconds.
    setPopoverBusy(true);
    // Stop the OUTGOING step's rect tracker and highlight guard before any
    // waiting starts. Without this, a step that waits on an async action result
    // or on a late-rendering target keeps the previous step's tracker refreshing
    // the overlay every 250ms for the whole wait (up to REQUIRED_STEP_WAIT_MS) —
    // flicker on a step the user has already left.
    clearTimers();
    const steps = stepsRef.current;

    // A click can start an asynchronous request whose failure is displayed by
    // the page. Do not let the capture-phase listener mark the tutorial complete
    // before that request has reported its outcome.
    const actionResult = steps[fromIndex] && steps[fromIndex].actionResult;
    if (actionResult) {
      const settled = await waitForTutorialActionResult(actionResult, {
        timeout: REQUIRED_STEP_WAIT_MS,
      });
      if (!mountedRef.current || driverRef.current !== driverObj) return setPopoverBusy(false);
      if (settled === ACTION_FAILURE) {
        // The page reported the action failed and is showing its own error. Leave
        // as an explicit failure so the analytics record does not claim the user
        // walked out.
        setPopoverBusy(false);
        outcomeRef.current = OUTCOME.ACTION_FAILED;
        driverObj.destroy();
        return;
      }
      if (settled !== ACTION_SUCCESS) {
        // TIMEOUT — we stopped waiting; we did NOT learn that anything failed.
        // Tearing the tour down here is what lost a slow-but-successful archive:
        // destroying the runner also unmounts the container's `completesOn`
        // listener, so a late success had nothing left to complete. Stand down
        // instead and leave both the tour and that listener alive — the emitted
        // result can still finish the lesson whenever it arrives, and the user can
        // retry or exit in the meantime.
        setPopoverBusy(false);
        return;
      }
    }

    let index = fromIndex + 1;

    while (index < steps.length) {
      const step = steps[index];
      const timeout = step.waitTimeout || (step.optional ? OPTIONAL_STEP_WAIT_MS : REQUIRED_STEP_WAIT_MS);
      // eslint-disable-next-line no-await-in-loop
      const found = await waitForStepElement(step, {
        timeout,
        signal: waitAbortRef.current,
      });
      if (!mountedRef.current || driverRef.current !== driverObj) return setPopoverBusy(false);
      if (found) break;
      if (!step.optional) {
        // A required target never appeared (the user dismissed the modal, or the
        // flow diverged). End cleanly rather than spotlighting nothing — but SAY
        // so: ending mutely after an 8s wait reads as "Next is broken", because
        // from the user's side they clicked Next and nothing happened for eight
        // seconds. Changing a filter is enough to cause it: the list refetches, so
        // the row the next step needs is briefly absent.
        setPopoverBusy(false);
        onUnavailable(step);
        outcomeRef.current = OUTCOME.STEP_UNAVAILABLE;
        driverObj.destroy();
        return;
      }
      index += 1;
    }

    if (index >= steps.length) {
      setPopoverBusy(false);
      outcomeRef.current = OUTCOME.COMPLETED;
      driverObj.destroy();
      return;
    }
    // Let the advancing click's re-render finish before the spotlight moves, so
    // driver.js measures a settled DOM (see STEP_SETTLE_MS).
    await wait(STEP_SETTLE_MS);
    if (!mountedRef.current || driverRef.current !== driverObj || !driverObj.isActive()) {
      return setPopoverBusy(false);
    }

    setPopoverBusy(false);
    driverObj.moveTo(index);

    // Self-heal only — repositioning is the rect tracker's job now.
    //
    // When the previous step's target is destroyed by the click that advanced us
    // (ticking a row checkbox re-renders the row), driver.js can land on the new
    // step with no active element at all. The overlay then has no cut-out and
    // its `.driver-active * { pointer-events: none }` rule makes the target
    // unclickable — the step becomes impossible to complete. Rebuilding the tour
    // at this step restores the highlight.
    //
    // These passes used to also call `refresh()` unconditionally, stacking two
    // more full overlay redraws on top of the tracker's and contributing to the
    // step-change flicker. The tracker handles movement; this only watches for
    // the broken-highlight case.
    //
    // Two passes: the record viewer keeps re-rendering its toolbar for a while
    // after the SPA navigation, so a single early check can pass and then the
    // node driver.js holds gets replaced. A detached node is still truthy, so
    // `isConnected` is checked explicitly.
    [300, 900].forEach((delay) => {
      schedule(() => {
        if (driverRef.current !== driverObj || !driverObj.isActive()) return;
        const activeEl = driverObj.getActiveElement();
        const stale = !activeEl || !activeEl.isConnected;
        if (stale && resolveStepElement(steps[index])) {
          requestRebuild(index);
        }
      }, delay);
    });
  }, [
    clearTimers, detachClickHandler, onBlocked, onUnavailable, requestRebuild,
    schedule, setPopoverBusy, wait,
  ]);

  /** Cursor choreography + click-to-advance wiring for the active step. */
  const handleHighlighted = useCallback((element, driverStep, opts) => {
    const step = (driverStep && driverStep.data && driverStep.data.step) || null;
    const index = typeof opts?.index === 'number' ? opts.index : 0;
    if (!step) return;

    // Refill the rebuild budget ONLY on a genuine step transition. A rebuild
    // re-enters this function at the same index, so it must find the same key —
    // that is the whole point of hanging the counter off a ref. Done before any
    // early return below, so a step that highlights with no measurable target
    // still gets a defined budget rather than inheriting the previous step's.
    const budgetKey = `${tutorial.id}:${index}`;
    if (rebuildBudgetRef.current.key !== budgetKey) {
      rebuildBudgetRef.current = { key: budgetKey, left: MAX_STEP_REBUILDS };
    }

    clearTimers();
    detachClickHandler();

    armPopoverGlide();
    const liveElement = resolveStepElement(step) || element;
    markActiveTarget(liveElement);
    const point = elementCenter(liveElement);
    if (!point) return;

    // Re-sync the spotlight cut-out after React has settled. Steps are reached
    // by clicking real UI, which frequently re-renders (and re-creates) the
    // surrounding nodes — a stale cut-out leaves the overlay covering the very
    // element the step wants clicked, which blocks the click entirely.
    //
    // A continuous tracker rather than a few fixed passes: an antd dropdown
    // item is measured mid slide-up animation (~300ms), and an antd modal can
    // keep growing well past a second as its content loads asynchronously. A
    // stale rect bakes the wrong cut-out into the overlay AND misplaces the
    // popover (which then overlaps — or swallows clicks meant for — the
    // target), and leaves the fake cursor stranded off-centre. Re-measure on
    // an interval for as long as the step is active (clearTimers() on the next
    // highlight/teardown stops it), refreshing the stage and re-gliding the
    // cursor whenever the target's centre actually moved.
    let lastCenter = null;
    // ONE unconditional refresh, and only once driver.js's highlight-transfer
    // animation has finished (TARGET_TRACK_START_MS). It corrects any popover
    // position baked in during the animation; every refresh after it is
    // change-driven. This was four unconditional refreshes on a 250ms cadence
    // starting at 250ms — i.e. squarely inside the animation — which is what
    // made every step change flicker.
    let unconditionalTicks = 1;
    const trackTarget = () => {
      const driverObj = driverRef.current;
      if (!driverObj || !driverObj.isActive()) return;
      const live = resolveStepElement(step);
      const settledPoint = elementCenter(live);
      const moved = settledPoint && (!lastCenter
        || Math.abs(settledPoint.x - lastCenter.x) > TARGET_MOVE_EPSILON_PX
        || Math.abs(settledPoint.y - lastCenter.y) > TARGET_MOVE_EPSILON_PX);
      if (moved || unconditionalTicks > 0) {
        unconditionalTicks -= 1;
        driverObj.refresh();
      }
      if (moved) {
        lastCenter = settledPoint;
        safeSetCursor({ point: settledPoint, pulsing: false, visible: true });
      }
      schedule(trackTarget, TARGET_TRACK_INTERVAL_MS);
    };
    schedule(trackTarget, TARGET_TRACK_START_MS);

    // Highlight guard. Hovering an antd menu item re-renders it and React
    // rewrites `className`, stripping driver.js's `driver-active-element`
    // class — pointer-events then die on the target, and the user's click
    // falls through to the overlay, which EXITS the tour (verified in a real
    // browser on the assign-record dropdown step). Keep re-asserting the class
    // on driver's own active element while the step is active.
    //
    // When hovering makes React REPLACE the node outright (sustained pointer
    // movement over an antd menu item does), the class re-add would hit a
    // detached node forever, so a disconnected active element rebuilds the
    // tour at this step — that re-resolves the target and re-attaches the
    // click-advance listener. The rebuild is gated on `isConnected` only:
    // comparing against a re-resolved element here proved too eager (equivalent
    // nodes resolve differently mid-render and the tour bounced between steps).
    // Consecutive ticks on which the target is both detached AND unresolvable.
    // A single tick is not enough to act on: a target is briefly in that state
    // during an ordinary re-render.
    let vanishedTicks = 0;
    const isLastStep = index >= stepsRef.current.length - 1;

    const guardHighlight = () => {
      const driverObj = driverRef.current;
      if (!driverObj || !driverObj.isActive()) return;
      const activeEl = driverObj.getActiveElement();
      if (activeEl && !activeEl.isConnected && resolveStepElement(step)) {
        // `requestRebuild` reports false once this step's budget is spent; then
        // stop rebuilding and let the vanished-target rules below reach a
        // definite outcome instead of looping forever.
        if (requestRebuild(index)) return;
      }

      // The final step's target is gone for good. Pure TEARDOWN: without this the
      // tour sits on a destroyed target forever, the popover having disappeared
      // with the modal.
      //
      // It deliberately reports no completion. A target vanishing is not evidence
      // that the taught action succeeded — cancelling the split dialog (which that
      // tutorial's own copy invites) and dismissing the assign modal destroy it
      // exactly the same way. Whether the lesson was finished is answered by the
      // tutorial's `completesOn` outcome, which is reported from redux and reaches
      // a listener that OUTLIVES this runner, so a late success still lands.
      if (!activeEl || !activeEl.isConnected) {
        vanishedTicks += 1;
        if (isLastStep && vanishedTicks >= VANISHED_TARGET_TICKS) {
          outcomeRef.current = OUTCOME.USER_EXITED;
          driverObj.destroy();
          return;
        }
        // A MID-tour target that is gone and cannot be re-resolved: the user
        // closed the dropdown the step was pointing into. Stop rather than leave
        // a spotlight sitting on whatever driver.js still holds — with a loose
        // selector that produced a cut-out on the filter bar while the popover
        // still read "Choose Assign Document". Say why; the checklist reopens.
        if (!isLastStep && vanishedTicks >= VANISHED_TARGET_TICKS && !resolveStepElement(step)) {
          setPopoverBusy(false);
          onUnavailable(step);
          outcomeRef.current = OUTCOME.STEP_UNAVAILABLE;
          driverObj.destroy();
          return;
        }
      } else {
        vanishedTicks = 0;
      }
      if (activeEl && activeEl.isConnected && !activeEl.classList.contains('driver-active-element')) {
        activeEl.classList.add('driver-active-element');
      }
      if (activeEl && activeEl.isConnected && !activeEl.hasAttribute('data-tutorial-highlight')) {
        markActiveTarget(activeEl);
      }
      schedule(guardHighlight, 200);
    };
    schedule(guardHighlight, 200);

    // Glide in, then pulse, then hand the click back to the user.
    safeSetCursor({ point, pulsing: false, visible: true });
    schedule(() => {
      safeSetCursor({ point, pulsing: true, visible: true });
      schedule(() => safeSetCursor({ point, pulsing: false, visible: true }), CURSOR_PULSE_MS);
    }, CURSOR_GLIDE_MS);

    if (step.advanceOn !== 'click' || !liveElement) return;

    // Detached explicitly on the click that really advances rather than with
    // `once`: a step whose gate is unsatisfied must stay armed for the user's next
    // attempt, and re-adding a `once` listener from inside its own invocation is
    // not something every DOM implementation honours. The flag keeps the original
    // guarantee that a re-render cannot double-advance.
    let advancing = false;
    const onClick = () => {
      if (advancing) return;
      // `advance` re-checks the gate and is the single place that reports it; this
      // only decides whether the listener may stand down.
      if (!evaluateStepGate(step)) {
        advancing = true;
        detachClickRef.current = null;
        liveElement.removeEventListener('click', onClick, { capture: true });
      }
      advance(index);
    };
    // Capture phase: the app's own handler still runs, we just also learn that
    // the click happened.
    liveElement.addEventListener('click', onClick, { capture: true });
    detachClickRef.current = () => liveElement.removeEventListener('click', onClick, { capture: true });
  }, [
    // `tutorial` is read for the vanished-target completion rule above. It comes
    // from a memoized list in the container, so its identity is stable across
    // renders — the same assumption the main effect below already depends on.
    tutorial,
    advance, armPopoverGlide, clearTimers, detachClickHandler, markActiveTarget,
    onUnavailable, requestRebuild, safeSetCursor, schedule, setPopoverBusy,
  ]);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    if (!tutorial) return undefined;

    outcomeRef.current = OUTCOME.USER_EXITED;
    // Fresh abort scope per tour, so this tour's DOM polls die with it.
    if (waitAbortRef.current) waitAbortRef.current.abort();
    waitAbortRef.current = typeof AbortController === 'function' ? new AbortController() : null;

    const steps = (Array.isArray(tutorial.steps) ? tutorial.steps : []).filter(Boolean);
    if (!steps.length) {
      // Nothing to show: report an exit so the container clears its active state
      // instead of leaving a tutorial that can never finish.
      onExit(tutorial.id);
      return undefined;
    }
    stepsRef.current = steps;

    // Declared up-front so the config's hooks can reference the instance and so
    // a broken transition can rebuild the tour at a given step (see rebuildAt).
    let driverObj;
    const createDriver = () => driver({
      animate: true,
      // Spotlight look: dim + a rounded cut-out around the target.
      overlayColor: '#0b0b0f',
      overlayOpacity: 0.6,
      stagePadding: 6,
      stageRadius: 8,
      smoothScroll: true,
      allowClose: true,
      // Clicking the dimmed backdrop exits — one more always-available escape.
      overlayClickBehavior: 'close',
      disableActiveInteraction: false,
      popoverClass: 'tutorial-popover',
      doneBtnText: 'Done',
      nextBtnText: 'Next',
      prevBtnText: 'Back',
      steps: buildDriverSteps(steps),
      // Every step must offer an obvious way out. driver.js only renders a
      // small "×", so a labelled "Skip tutorial" is added next to the progress
      // text — grouped with it so the footer stays balanced.
      onPopoverRender: (popover) => {
        const { footer, progress } = popover;
        if (!footer || footer.querySelector('.tutorial-popover__skip')) return;

        const left = document.createElement('div');
        left.className = 'tutorial-popover__footer-left';
        if (progress && progress.parentNode === footer) left.appendChild(progress);

        const skip = document.createElement('button');
        skip.type = 'button';
        skip.className = 'tutorial-popover__skip';
        skip.textContent = 'Skip tutorial';
        skip.addEventListener('click', () => {
          outcomeRef.current = OUTCOME.USER_EXITED;
          if (driverRef.current) driverRef.current.destroy();
        });
        left.appendChild(skip);
        footer.insertBefore(left, footer.firstChild);
      },
      onHighlighted: handleHighlighted,
      // Take over navigation so we can wait for late-rendering targets.
      onNextClick: () => {
        const index = driverObj.getActiveIndex() || 0;
        advance(index);
      },
      onPrevClick: () => {
        detachClickHandler();
        driverObj.movePrevious();
      },
      onDoneClick: () => {
        outcomeRef.current = OUTCOME.COMPLETED;
        driverObj.destroy();
      },
      onCloseClick: () => {
        outcomeRef.current = OUTCOME.USER_EXITED;
        driverObj.destroy();
      },
      onDestroyed: () => {
        clearTimers();
        detachClickHandler();
        // driver.js removes its popover on destroy; drop the reference so the
        // next tour arms its own fresh node instead of assuming it is armed.
        popoverNodeRef.current = null;
        markActiveTarget(null);
        safeSetCursor({ point: null, pulsing: false, visible: false });
        // ONE read of ONE value — the whole point of OUTCOME.
        const outcome = outcomeRef.current;
        if (outcome === OUTCOME.SILENT) return;
        if (outcome === OUTCOME.COMPLETED) onComplete(tutorial.id);
        // STEP_UNAVAILABLE has already been reported by onUnavailable, which shows
        // the reason; it still needs the exit so the container clears its state.
        else onExit(tutorial.id, outcome);
      },
    });

    /**
     * Recreates the tour and drives it straight to `index`.
     *
     * driver.js can land on a step with no active element when the *previous*
     * step's target was destroyed by the click that advanced us (ticking a row
     * checkbox re-renders the row). With no active element the overlay has no
     * cut-out, and its `.driver-active * { pointer-events: none }` rule makes
     * the intended target unclickable — the step is impossible to finish.
     * moveTo/refresh do not recover it, but a fresh drive() always highlights
     * correctly, so the tour is rebuilt in place. Silent so the teardown does
     * not report a completion or an exit the user did not make.
     */
    rebuildAtRef.current = (index) => {
      const previous = driverRef.current;
      outcomeRef.current = OUTCOME.SILENT;
      if (previous && previous.isActive()) previous.destroy();
      outcomeRef.current = OUTCOME.USER_EXITED;
      driverObj = createDriver();
      driverRef.current = driverObj;
      driverObj.drive(index);
    };

    driverObj = createDriver();
    driverRef.current = driverObj;
    driverObj.drive();

    return () => {
      // Unmount / route change / tutorial swap: tear the overlay down without
      // reporting a completion or an exit the user didn't make.
      outcomeRef.current = OUTCOME.SILENT;
      clearTimers();
      if (waitAbortRef.current) waitAbortRef.current.abort();
      detachClickHandler();
      if (driverObj.isActive()) driverObj.destroy();
      driverRef.current = null;
    };
  }, [
    tutorial,
    onComplete,
    onExit,
    advance,
    clearTimers,
    detachClickHandler,
    handleHighlighted,
    markActiveTarget,
    safeSetCursor,
  ]);

  return cursor;
};

const TutorialRunner = ({
  tutorial, onComplete, onExit, onUnavailable, onBlocked,
}) => {
  const cursor = useTutorialDriver({
    tutorial, onComplete, onExit, onUnavailable, onBlocked,
  });

  return (
    <AnimatedCursor point={cursor.point} pulsing={cursor.pulsing} visible={cursor.visible} />
  );
};

TutorialRunner.defaultProps = {
  tutorial: null,
  onUnavailable: () => {},
  onBlocked: () => {},
};

TutorialRunner.propTypes = {
  tutorial: PropTypes.shape({
    id: PropTypes.string.isRequired,
    steps: PropTypes.array.isRequired,
  }),
  onComplete: PropTypes.func.isRequired,
  onExit: PropTypes.func.isRequired,
  // Called when a REQUIRED step's target never appears, so the container can say
  // why the tour stopped instead of it vanishing mutely.
  onUnavailable: PropTypes.func,
  // Called with a reason when a step's gate refuses to let the tour advance. The
  // tutorial keeps running — this only asks the container to say why.
  onBlocked: PropTypes.func,
};

export default TutorialRunner;
